import { useMemo } from 'react';
import {
  ChartSortEnum,
  IChartSettings,
  IParsedChartDataRetention,
  IRetentionTable,
  RetentionTableType,
} from '../../interfaces';
import { UnreachableCaseError, ValueOf } from 'ts-essentials';
import { MaybeNil } from '../../../../utils/interfaces';
import isNil from 'lodash/isNil';
import produce from 'immer';

interface IProps {
  chartSettings: IChartSettings;
  chartData: IParsedChartDataRetention;
  onlyVisibleMeasures: boolean;
}

type IRetentionTableRow = ValueOf<IRetentionTable['rows']>;

const filterByPredicates = <T>(
  rows: T[],
  predicates: Array<(row: T) => boolean>,
): T[] => rows.filter((row) => predicates.every((predicate) => predicate(row)));

const sliceKeyPredicate =
  (visibleSliceKeys: string[]) => (row: IRetentionTableRow) =>
    visibleSliceKeys.includes(row.sliceKey);

const measureKeyPredicate =
  (singleDayLabel: MaybeNil<string>, visibleMeasureKeys: MaybeNil<string[]>) =>
  (row: IRetentionTableRow) => {
    if (!isNil(singleDayLabel) && singleDayLabel.trim().length > 0) {
      return row.header.key === singleDayLabel;
    } else if (!isNil(visibleMeasureKeys)) {
      return visibleMeasureKeys.includes(row.header.key);
    } else {
      return true;
    }
  };

const rowHeaderComparator = <T extends IRetentionTableRow>(a: T, b: T) =>
  a.header.index - b.header.index;

const noopComparator = () => 0;

const sortDirectionComparator = <T>(
  comparator: (a: T, b: T) => number,
  sort: ChartSortEnum,
) => {
  if (sort === ChartSortEnum.ASC) {
    return comparator;
  } else if (sort === ChartSortEnum.DESC) {
    return (a: T, b: T) => comparator(a, b) * -1;
  } else if (sort === ChartSortEnum.NONE) {
    return noopComparator;
  } else {
    throw new UnreachableCaseError(sort);
  }
};

const filterByChartSettings = <Row extends IRetentionTableRow>(
  rows: Row[],
  visibleSliceKeys: string[],
  singleDayLabel: MaybeNil<string>,
  visibleMeasureKeys: MaybeNil<string[]>,
  sort: ChartSortEnum,
): Row[] => {
  const filteredRows = filterByPredicates(rows, [
    sliceKeyPredicate(visibleSliceKeys),
    measureKeyPredicate(singleDayLabel, visibleMeasureKeys),
  ]);

  filteredRows.sort(sortDirectionComparator(rowHeaderComparator, sort));

  return filteredRows;
};

/**
 * Custom hook that:
 * 1. according to visible slices,
 *    extends each retention row with sliceKey and dateIndex fields
 * 2. handles sorting
 * 3. handles singleDayLabel
 */
export default ({ chartSettings, chartData, onlyVisibleMeasures }: IProps) => {
  const {
    sort,
    displayedSliceSettings,
    displayedMeasureSettings,
    singleDayLabel,
  } = chartSettings;
  return useMemo((): IRetentionTable => {
    const { table } = chartData;

    // get all visible slices
    const visibleSliceKeys = displayedSliceSettings
      .filter((slice) => slice.isVisible)
      .map((slice) => slice.key);

    let visibleMeasureKeys: MaybeNil<string[]> = null;
    if (onlyVisibleMeasures) {
      // get all visible slices
      visibleMeasureKeys = displayedMeasureSettings
        .filter((measure) => measure.isVisible)
        .map((measure) => measure.key);
    }

    return produce(table, (draft) => {
      // The code in each `if` body is the same to ensure type safety. `rows`
      // can be either A[] | B[] ("an array of only A" or "array of only B").
      // If we apply generic filter on this type, we get
      // (A | B)[] ("an array of either A or B"). This does not satisfy the
      // initial `rows` type "an array of either A or B" is not the same as
      // "an array of only A"
      if (draft.type === RetentionTableType.Standard) {
        draft.rows = filterByChartSettings(
          draft.rows,
          visibleSliceKeys,
          singleDayLabel,
          visibleMeasureKeys,
          sort,
        );
      } else if (draft.type === RetentionTableType.OverTime) {
        draft.rows = filterByChartSettings(
          draft.rows,
          visibleSliceKeys,
          singleDayLabel,
          visibleMeasureKeys,
          sort,
        );
      } else {
        throw new UnreachableCaseError(draft);
      }
    });
  }, [
    chartData,
    onlyVisibleMeasures,
    singleDayLabel,
    displayedMeasureSettings,
    displayedSliceSettings,
    sort,
  ]);
};
